import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiLayers, FiArrowRight, FiCheckSquare, FiGrid, FiCalendar, FiBarChart2,
  FiBell, FiMessageSquare, FiZap, FiClock, FiShare2, FiGitBranch, FiAlertTriangle
} from 'react-icons/fi';
import PublicPageHeader from '../../components/common/PublicPageHeader';
import PublicPageFooter from '../../components/common/PublicPageFooter';
import DocsSidebar from '../../components/docs/DocsSidebar';

const FeaturesOverview = () => {
  const implementedFeatures = [
    {
      icon: FiCheckSquare,
      title: 'Core Task Management',
      description: 'Create, organize, and track tasks with priorities, due dates, labels, and subtasks.',
      status: 'available',
      link: '/docs/personal/tasks'
    },
    {
      icon: FiGrid,
      title: 'Multiple Views',
      description: 'Kanban boards, list views, calendar views, and table views for every project.',
      status: 'available',
      link: '/docs/features/views'
    },
    {
      icon: FiBell,
      title: 'Notifications',
      description: 'Real-time notifications for task assignments, comments, and due date reminders.',
      status: 'available',
      link: '/docs/features/notifications'
    },
    {
      icon: FiMessageSquare,
      title: 'Team Collaboration',
      description: 'Comments, mentions, activity feeds, and real-time collaboration on tasks.',
      status: 'available',
      link: '/docs/personal/teams'
    },
    {
      icon: FiCalendar,
      title: 'Calendar Integration',
      description: 'View tasks by due date on a calendar. Google Calendar sync available.',
      status: 'available',
      link: '/docs/features/calendar'
    },
    {
      icon: FiBarChart2,
      title: 'Analytics & Reports',
      description: 'Progress analytics, productivity stats, and customizable reports.',
      status: 'available',
      link: '/docs/features/analytics'
    },
    {
      icon: FiZap,
      title: 'AI Assistance',
      description: 'AI-powered insights for task suggestions and productivity recommendations.',
      status: 'available',
      link: '/docs/features/ai'
    },
    {
      icon: FiClock,
      title: 'Timeline / Gantt View',
      description: 'Visual timeline for project planning with day, week, and month views.',
      status: 'available',
      link: '/docs/features/timeline'
    },
    {
      icon: FiGitBranch,
      title: 'Task Dependencies',
      description: 'Link tasks with blocking dependencies and enforce execution order.',
      status: 'available',
      link: '/docs/features/dependencies'
    }
  ];

  const comingSoonFeatures = [
    {
      icon: FiShare2,
      title: 'Automation Rules',
      description: 'Trigger-based automation with custom workflows and rule templates.',
      status: 'coming-soon',
      roadmapDate: 'Q2 2026'
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/features" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Features</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-green-100 dark:bg-green-900/30 rounded-xl mb-4">
              <FiLayers className="w-6 h-6 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Features Overview</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Explore all the features Tasklina offers to help you and your team stay productive.
            </p>
          </div>

          {/* Available Features */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Available Features</h2>
            <div className="space-y-4">
              {implementedFeatures.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Link
                    key={index}
                    to={feature.link}
                    className="flex items-start space-x-4 p-4 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-green-300 dark:hover:border-green-700 hover:shadow-sm transition-all"
                  >
                    <div className="w-12 h-12 bg-green-50 dark:bg-green-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-semibold text-gray-900 dark:text-white">{feature.title}</h3>
                        <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-xs rounded-full">
                          Available
                        </span>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 text-sm">{feature.description}</p>
                    </div>
                    <FiArrowRight className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  </Link>
                );
              })}
            </div>
          </section>

          {/* Coming Soon Features */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Coming Soon</h2>
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded-xl p-4 mb-6">
              <div className="flex items-start space-x-3">
                <FiAlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-900 dark:text-amber-200">Roadmap Features</h4>
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    These features are in development and will be available in upcoming releases.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {comingSoonFeatures.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="flex items-start space-x-4 p-4 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800"
                  >
                    <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-gray-500 dark:text-gray-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-semibold text-gray-900 dark:text-white">{feature.title}</h3>
                        <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-xs rounded-full">
                          {feature.roadmapDate}
                        </span>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 text-sm">{feature.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Feature Comparison */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Feature Availability by Plan</h2>
            <div className="overflow-hidden border border-gray-200 dark:border-gray-700 rounded-xl">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Feature</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">Free</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">Pro</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">Enterprise</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">Unlimited Tasks</td>
                    <td className="px-4 py-3 text-center"><span className="text-green-500">✓</span></td>
                    <td className="px-4 py-3 text-center"><span className="text-green-500">✓</span></td>
                    <td className="px-4 py-3 text-center"><span className="text-green-500">✓</span></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">Projects</td>
                    <td className="px-4 py-3 text-center text-sm text-gray-600 dark:text-gray-300">5</td>
                    <td className="px-4 py-3 text-center"><span className="text-green-500">Unlimited</span></td>
                    <td className="px-4 py-3 text-center"><span className="text-green-500">Unlimited</span></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">Team Members</td>
                    <td className="px-4 py-3 text-center text-sm text-gray-600 dark:text-gray-300">3</td>
                    <td className="px-4 py-3 text-center text-sm text-gray-600 dark:text-gray-300">25</td>
                    <td className="px-4 py-3 text-center"><span className="text-green-500">Unlimited</span></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">Departments</td>
                    <td className="px-4 py-3 text-center text-gray-400 dark:text-gray-500">—</td>
                    <td className="px-4 py-3 text-center text-sm text-gray-600 dark:text-gray-300">5</td>
                    <td className="px-4 py-3 text-center"><span className="text-green-500">Unlimited</span></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">Advanced Analytics</td>
                    <td className="px-4 py-3 text-center text-gray-400 dark:text-gray-500">—</td>
                    <td className="px-4 py-3 text-center"><span className="text-green-500">✓</span></td>
                    <td className="px-4 py-3 text-center"><span className="text-green-500">✓</span></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">Custom Roles</td>
                    <td className="px-4 py-3 text-center text-gray-400 dark:text-gray-500">—</td>
                    <td className="px-4 py-3 text-center text-gray-400 dark:text-gray-500">—</td>
                    <td className="px-4 py-3 text-center"><span className="text-green-500">✓</span></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">SSO / SAML</td>
                    <td className="px-4 py-3 text-center text-gray-400 dark:text-gray-500">—</td>
                    <td className="px-4 py-3 text-center text-gray-400 dark:text-gray-500">—</td>
                    <td className="px-4 py-3 text-center"><span className="text-green-500">✓</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-4 text-center">
              <Link to="/pricing" className="text-blue-600 hover:underline text-sm font-medium">
                View full pricing details →
              </Link>
            </div>
          </section>

          {/* Navigation */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Explore Features</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/features/views"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 border border-transparent hover:border-blue-200 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Views & Boards</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/features/calendar"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 border border-transparent hover:border-blue-200 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Calendar Integration</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/features/notifications"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 border border-transparent hover:border-blue-200 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Notifications</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/features/ai"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 border border-transparent hover:border-blue-200 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">AI Assistance</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Article Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Last updated: January 27, 2026
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                  Yes
                </button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                  No
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default FeaturesOverview;
